import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useHistory, Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Link from '@material-ui/core/Link';
import CreateJobForm from '../CreateJobForm/CreateJobForm';
import { useDispatch, useSelector } from 'react-redux';

import { jobRequest, getJobRequest, updateJobRequest } from '../../actions';
import Loader from '../Loader/Loader';
import axios from '../../services';
import mapQuotationToCreateJobForm from '../../utils/mapQuotationToCreateJobForm';
import toaster from '../../utils/toaster';

const useStyles = makeStyles((theme) => ({
  page: {
    maxWidth: 1360,
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(6),
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: theme.palette.text.primary,
  },
  subtitle: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontWeight: 400,
    letterSpacing: '0.01em',
    lineHeight: 1.5,
  },
  hint: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
  exploreCard: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
  exploreList: {
    margin: theme.spacing(1, 0, 0, 0),
    paddingLeft: theme.spacing(2.5),
    '& li': {
      marginBottom: theme.spacing(0.75),
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
}));

const CreateJobPage = () => {
  
  const classes = useStyles();
  const dispatch = useDispatch();
  const { jobId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const quotationId = useMemo(
    () => new URLSearchParams(location.search).get('quotationId'),
    [location.search]
  );

  const [quotationPrefill, setQuotationPrefill] = useState(null);
  const [platformGuideOpen, setPlatformGuideOpen] = useState(false);

  const isEditing = !!jobId;

  const { job } = useSelector((state) => state.Job);

  // Fetch on mount so direct-URL access works (AllJobsPage also does this before navigating).
  useEffect(() => {
    if (!isEditing) return;
    dispatch(getJobRequest(jobId));
  }, [isEditing, jobId, dispatch]);

  // jobReady: form is safe to render only when Redux has the right job loaded.
  // Derived directly — no local state, no timing issues.
  const jobReady = !isEditing || (
    !!job &&
    (String(job?.gcnno) === String(jobId) || String(job?.carGcnno) === String(jobId))
  );

  useEffect(() => {
    if (!quotationId || isEditing) {
      setQuotationPrefill(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`quotations/${quotationId}`)
      .then((res) => {
        if (!cancelled) setQuotationPrefill(mapQuotationToCreateJobForm(res.data));
      })
      .catch((err) => {
        if (!cancelled) {
          toaster(err?.response?.data?.message || 'Could not load quotation for prefill', { type: 'error' });
          setQuotationPrefill(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [quotationId, isEditing]);

  const onCreateJob = (data) => {
    const payload = { ...data };
    if (quotationId) payload.sourceQuotationId = quotationId;
    dispatch(
      jobRequest(payload, {
        onSuccess: (createdJob) => {
          const createdGcn = createdJob?.gcnno || createdJob?.carGcnno || '';
          const params = new URLSearchParams();
          if (createdGcn) params.set('highlight', String(createdGcn));
          params.set('created', '1');
          history.push(`/jobs?${params.toString()}`);
        },
      })
    );
  };

  const onUpdateJob = (data) => {
    dispatch(
      updateJobRequest(data, jobId, {
        onSuccess: () => {
          history.push(`/jobs?highlight=${encodeURIComponent(jobId)}`);
        },
      })
    );
  };

  const jobToUpdate = job;

  return (
    <Box className={classes.page}>
      <header className={classes.header}>
        <Typography variant='h4' component='h1' className={classes.title}>
          {isEditing ? 'Update job' : 'Create job'}
        </Typography>
        <Typography variant='body1' className={classes.subtitle}>
          Enter consignment and contact details. Required fields are marked on the form.
        </Typography>
        {quotationId && !isEditing && (
          <Typography variant='body2' className={classes.hint}>
            Prefilled from quotation{quotationPrefill ? '' : ' (loading…)'} — review all fields before submitting.
          </Typography>
        )}
        <Paper elevation={0} className={classes.exploreCard}>
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
            What else you can do on Ace
          </Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginTop: 8 }}>
            Jobs are one piece of the workflow. Most teams pair them with quotes, barcodes, and a shared directory.
          </Typography>
          <ul className={classes.exploreList}>
            <li>
              <Link component={RouterLink} to='/cft-calculator' color='primary'>
                Generate quotation
              </Link>{' '}
              — build CFT-based quotes (optionally from Excel) before or after this job.
            </li>
            <li>
              <Link component={RouterLink} to='/quotations' color='primary'>
                Quotations list
              </Link>{' '}
              — open pricing/PDF or copy a customer intake link after you save a quote.
            </li>
            <li>
              <Link component={RouterLink} to='/create-barcode' color='primary'>
                Create barcode
              </Link>{' '}
              and{' '}
              <Link component={RouterLink} to='/barcode' color='primary'>
                barcode library
              </Link>{' '}
              for warehouse tracking.
            </li>
            <li>
              <Link component={RouterLink} to='/operations/contacts' color='primary'>
                People directory
              </Link>{' '}
              — save partners, transporters, and insurers once, then link them here.
            </li>
            <li>
              <Link component={RouterLink} to='/jobs' color='primary'>
                All jobs
              </Link>{' '}
              — search GCN, invoices, and move status from the table.
            </li>
            <li>
              <Link component={RouterLink} to='/operations/team' color='primary'>
                Invite team
              </Link>{' '}
              (admins) and{' '}
              <Link component={RouterLink} to='/settings/organization' color='primary'>
                organization branding
              </Link>{' '}
              for customer-facing PDFs.
            </li>
          </ul>
          <Button size='small' color='primary' onClick={() => setPlatformGuideOpen(true)}>
            Open step-by-step checklist
          </Button>
        </Paper>
      </header>
      {!jobReady ? (
        <Loader />
      ) : (
        <CreateJobForm
          key={jobId || 'new'}
          onCreateJob={onCreateJob}
          onUpdateJob={onUpdateJob}
          isEditing={isEditing}
          job={jobToUpdate}
          quotationPrefill={quotationPrefill}
        />
      )}
      <Dialog open={platformGuideOpen} onClose={() => setPlatformGuideOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Typical flow after a job</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='textSecondary' paragraph>
            There is no single required order — use what fits your customer. Many teams:
          </Typography>
          <ol style={{ marginTop: 0, paddingLeft: 20, color: 'inherit' }}>
            <li>
              <Typography variant='body2' color='textSecondary' component='span'>
                Start from a quotation when pricing is still open, then open this job with a quotation link so fields stay aligned.
              </Typography>
            </li>
            <li>
              <Typography variant='body2' color='textSecondary' component='span'>
                Issue barcodes when packing begins so scanning matches the consignment on this job.
              </Typography>
            </li>
            <li>
              <Typography variant='body2' color='textSecondary' component='span'>
                Use the header search for people and invites; use each list’s search for jobs and quotations.
              </Typography>
            </li>
            <li>
              <Typography variant='body2' color='textSecondary' component='span'>
                Click the floating Help (?) to replay the platform tour or see a tip for the page you are on.
              </Typography>
            </li>
          </ol>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlatformGuideOpen(false)} color='primary' variant='contained'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateJobPage;
